import PropTypes from 'prop-types';

import Tag from '#globalComponents/Tag/Tag';

import { useUpdateCompanyTags } from '../../../models/tags/mutations';

import TagOverflowPopover from './TagOverflowPopover';

const MAX_VISIBLE_TAGS = 1;

/**
 * Tags cell for the company table: shows up to MAX_VISIBLE_TAGS chips inline and collapses the
 * rest into a "+N" popover. Tags can be removed inline or from the popover via the chip's ✕,
 * which persists the reduced tag list with an optimistic update (useUpdateCompanyTags). Owning
 * the mutation here avoids drilling a remove callback through the table/row.
 */
const CompanyTagsCell = ({ company }) => {
	const tags = company.tags ?? [];
	const updateTags = useUpdateCompanyTags();

	const removeTag = (tagId) =>
		updateTags.mutate({
			companyId: company.id,
			tags: tags.filter((t) => t.id !== tagId),
		});

	if (tags.length === 0) return <span className="text-bgBase-800">–</span>;

	const visible = tags.slice(0, MAX_VISIBLE_TAGS);
	const overflow = tags.slice(MAX_VISIBLE_TAGS);

	return (
		<div className="flex flex-nowrap items-center gap-1">
			{visible.map((tag) => (
				<Tag key={tag.id} label={tag.name} size="xs" hoverRemove onRemove={() => removeTag(tag.id)} />
			))}
			{overflow.length > 0 && (
				<TagOverflowPopover tags={overflow} count={overflow.length} onRemoveTag={removeTag} />
			)}
		</div>
	);
};

CompanyTagsCell.propTypes = {
	company: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		tags: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
				name: PropTypes.string.isRequired,
			}),
		),
	}).isRequired,
};

export default CompanyTagsCell;
